import { AppointmentTypesApi } from '@frontend/api-appointment-types';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { settingsTrackingIds } from '@frontend/schedule-helpers';
import { SettingsLink, useSettingsNavigate } from '@frontend/settings-routing';
import { SettingsGroup } from '@frontend/settings-ui';
import { IndexRow, SpinningLoader, Text, styles } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';

type MultiLocationServiceTypesProps = {
  shouldRenderV3Settings?: boolean;
};

export const MultiLocationServiceTypes = ({ shouldRenderV3Settings = false }: MultiLocationServiceTypesProps) => {
  const { navigate } = useSettingsNavigate();
  const { t } = useTranslation('schedule-settings');
  const { selectedLocationIds, getScopeName, parentLocationId } = useSchedulingLocationInfo();

  const { data: appointmentTypesMultiData, isLoading: isLoadingAppointmentTypesData } =
    SchedulerV3.Queries.useListAppointmentTypesMultiQuery({
      request: {
        locationsIds: selectedLocationIds || [],
        parentLocationId: parentLocationId || '',
      },
      options: {
        enabled: shouldRenderV3Settings && !!selectedLocationIds?.length && !!parentLocationId,
      },
    });

  const { data: appointmentTypesCountData, isLoading: isLoadingAppointmentTypesCount } = useQuery({
    queryKey: ['appointment-types-count', ...selectedLocationIds, !shouldRenderV3Settings],
    queryFn: () =>
      AppointmentTypesApi.getAppointmentTypesCount({
        locationIds: selectedLocationIds,
      }),
    enabled: !shouldRenderV3Settings,
    cacheTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const appointmentTypeV3CountByLocationId = appointmentTypesMultiData?.appointmentTypes?.reduce(
    (acc, { locationId }) => {
      if (locationId && !acc[locationId]) {
        acc[locationId] = 1 || 0;
      } else {
        acc[locationId] += 1;
      }
      return acc;
    },
    {} as { [key: string]: number }
  );

  const appointmentTypesCountByLocationId = appointmentTypesCountData?.appointmentTypesCount?.reduce(
    (acc, { locationId, count }) => {
      if (locationId && !acc[locationId]) {
        acc[locationId] = count || 0;
      }
      return acc;
    },
    {} as { [key: string]: number }
  );

  const appointmentTypesCount = shouldRenderV3Settings
    ? appointmentTypeV3CountByLocationId
    : appointmentTypesCountByLocationId;

  const navigateTo = (locationId: string) => {
    const route = '/schedule/services/:locationId';

    navigate({
      to: route,
      params: { locationId: locationId ?? '' },
    });
  };

  if (isLoadingAppointmentTypesCount || isLoadingAppointmentTypesData) {
    return (
      <div css={styles.flexCenter}>
        <SpinningLoader />
      </div>
    );
  }

  return (
    <SettingsGroup title={t('Location Settings')}>
      <IndexRow>
        {selectedLocationIds?.map((locationId) => (
          <IndexRow.Item
            key={locationId}
            as={SettingsLink}
            to='/schedule/services/:locationId'
            onClick={() => navigateTo(locationId)}
            trackingId={settingsTrackingIds.serviceTypesLocationRowItem}
          >
            <IndexRow.Item.Title>
              <div css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Text>{getScopeName(locationId)}</Text>
                {!shouldRenderV3Settings && (
                  <Text size='medium' color='light' css={{ marginLeft: 'auto' }}>
                    {appointmentTypesCount?.[locationId] === 1
                      ? t(`1 Appointment Type`)
                      : t(`{{count}} Appointment Types`, { count: appointmentTypesCount?.[locationId] ?? 0 })}
                  </Text>
                )}
              </div>
            </IndexRow.Item.Title>
          </IndexRow.Item>
        ))}
      </IndexRow>
    </SettingsGroup>
  );
};
